import React, { Suspense, lazy } from 'react';

/**
 * Lazy load the component.
 *
 * No Suspense fallback is rendered: chunk loads are brief, and pages provide
 * their own loading state (skeletons), so showing a spinner here would only
 * cause a flash before the page's skeleton appears.
 *
 * @param {*} Component The component to render.
 *
 * @returns {*} The wrapped component.
 */
const withSuspense = (Component) => {
  const WithSuspense = (props) => (
    <Suspense fallback={null}>
      <Component {...props} />
    </Suspense>
  );

  WithSuspense.displayName = `withSuspense(${Component.displayName || Component.name || 'Component'})`;

  return WithSuspense;
};

/**
 * Allow a component to render by waiting for the callback.
 *
 * @param {Function} callback The callback to wait for.
 *
 * @returns {*} The rendered component.
 */
export default (callback) => withSuspense(lazy(callback));

/**
 * Lazy-load a route page WITHOUT a local Suspense boundary.
 *
 * Page chunks suspend up to the single persistent boundary around the routes
 * (in Core). Because route changes are wrapped in startTransition (router
 * v7_startTransition flag), React then keeps the CURRENT page on screen until
 * the next page's chunk is ready — no blank content between pages.
 *
 * Use the default export for modals and other on-demand components instead:
 * those load outside a transition, so they need their own local boundary.
 *
 * @param {Function} callback The import callback.
 *
 * @returns {*} The lazy component.
 */
export const lazyPage = (callback) => lazy(callback);
